import AttributeSelectDropdown from 'components/order-entry/Products/Inputs/AttributeSelectDropdown/AttributeSelectDropdown';
import { useOrderEntryContext } from 'providers/OrderModuleProvider';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { DropdownModel, LocalOrderProductAttributeInput } from 'shared/models';
import { useFetchCacheStore } from 'stores/useFetchCacheStore';
import { LocalAttributeAggregate, ProductFullAttributeExtended } from 'types/order-entry';
import { AggregateChildAttribute } from './AggregateChildAttribute/AggregateChildAttribute';
import { findAttributeCondition, findAttributeConditionIndex, getAttributeNamesFromAttributeRules } from './helper';
import { InitialDisplayAttribute } from './InitialDisplayAttribute';

export type AggregateAttributeTypeEnumProps = Omit<LocalAttributeAggregate, 'attributeValueType'> & {
  onValueChange?: (currentValue: string, previousValue: string) => void;
};

export const AggregateAttributeTypeEnum: FC<AggregateAttributeTypeEnumProps> = ({
  attributeOptions,
  localTypeValue,
  type: attributeType,
  displayName,
  selectedTooth,
  defaultValue,
  isRequired,
  orderItemId,
  attributeRules,
  isShowDisplayAttributeByDefault,
  onValueChange,
}) => {
  const { order: localOrderData, updateOrderItem, onSubmitAlertCount, orderData } = useOrderEntryContext();

  const options = useMemo<DropdownModel[]>(() => {
    return attributeOptions.map(option => {
      return { value: option, primaryLabel: option };
    });
  }, [attributeOptions]);

  const selectedOrderItem = useMemo(() => {
    return localOrderData?.orderItems.find(item => item.id === orderItemId);
  }, [localOrderData?.orderItems, orderItemId]);

  const productCode = selectedOrderItem?.productCode || '';
  const findProductFullAttributeByOption = useFetchCacheStore(state => state.findProductFullAttributeByOption);

  const selectedLocalAttribute = useMemo(() => {
    return findAttributeCondition(selectedOrderItem, localTypeValue, selectedTooth);
  }, [localTypeValue, selectedOrderItem, selectedTooth]);

  const hasOption = useCallback(
    (name?: string) => options.some(o => o.value.toLowerCase() === name?.toLowerCase()),
    [options]
  );

  const hasValidName = useCallback(
    (name: string) => {
      const hasValidOption = hasOption(name);
      if (isRequired) {
        // if the field is required, `name` should be non-empty and has to be a valid option
        return !!name && hasValidOption;
      } else {
        // if the field is not required, then
        // 1. if the `name` is empty, this function returns true,
        // 2. if the `name` is empty, it has to be a valid option.
        return name ? hasValidOption : true;
      }
    },
    [isRequired, hasOption]
  );

  const nameValue = useMemo(() => {
    const name = selectedLocalAttribute?.name || '';

    // remove default value, if the value provided is absent in options
    // refer https://glidewell.atlassian.net/browse/LMS1-8753 for more details
    return hasValidName(name) ? name : '';
  }, [hasValidName, selectedLocalAttribute?.name]);

  const getDefaultAttributeItem = useCallback(
    name => {
      /**
       * NOTE: The field `localTypeValue` comes from the custom built attribute and not the attribute being referenced in the attributeOptions array.
       * attributeOptions are really pointers to attributes that were returned when fetching product information.
       * Futurely, we may need to get the type directly from the attribute in case the actual attribute type can differ from the customized attribute type set
       * when building the attribute.
       *
       * Push the new attribute to the selected order item
       */
      const localInput: LocalOrderProductAttributeInput = {
        name: name,
        type: localTypeValue,
        value: selectedTooth,
        quantity: 1,
        isInvalid: !hasValidName(name),
      };
      return localInput;
    },
    [hasValidName, localTypeValue, selectedTooth]
  );

  /**
   * Updates the name value of the selected attribute.
   * If the attribute is required, sets the isInvalid flag to true if the value is empty.
   * Removes attributes that are in the displayAttributeNames array from the selected order item.
   * Updates the order item with the updated attributes.
   *
   * @param currentValue - The new value for the attribute name.
   * @param previousValue - The previous value of the attribute name.
   */
  const onUpdateNameValue = useCallback(
    (currentValue: string, previousValue: string) => {
      if (!selectedOrderItem) return;

      if (selectedLocalAttribute) {
        selectedLocalAttribute.name = currentValue;
        selectedLocalAttribute.isInvalid = !hasValidName(currentValue);
      } else {
        selectedOrderItem.attributes.push(getDefaultAttributeItem(currentValue));
      }

      const productFullAttribute = findProductFullAttributeByOption(productCode, {
        name: previousValue,
        type: attributeType,
      });

      const displayAttributeNames = getAttributeNamesFromAttributeRules(productFullAttribute?.attributeRules || []);

      if (displayAttributeNames.length) {
        // Remove previously selected attributes if selectedTooth is equal to the attribute value and the attribute name or attribute type is in the displayAttributeNames array
        selectedOrderItem.attributes = selectedOrderItem.attributes.filter(attr => {
          /**
           * Check if the attribute name is in the displayAttributeNames array or the attribute type is in the displayAttributeNames array.
           * For Non-Inclusive/Inclusive Implant System, the attribute name is the type of the attribute.
           * We made this change to fix the issue where the attribute name is not in the displayAttributeNames array.
           * Issue ticket link - https://glidewell.atlassian.net/browse/LMS1-7363
           */
          const isAttributeExist =
            displayAttributeNames.includes(attr.name) || displayAttributeNames.includes(attr.type);
          if (attr.value === selectedTooth && isAttributeExist) return false;
          return true;
        });
      }

      updateOrderItem(selectedOrderItem.id, { attributes: selectedOrderItem.attributes });
    },
    [
      attributeType,
      findProductFullAttributeByOption,
      getDefaultAttributeItem,
      productCode,
      selectedLocalAttribute,
      selectedOrderItem,
      selectedTooth,
      hasValidName,
      updateOrderItem,
    ]
  );

  /**
   * Create a new attribute if it doesn't exist
   */
  useEffect(() => {
    if (!selectedOrderItem) return; // if there is no selected order item, do nothing

    const foundAttribute = findAttributeCondition(selectedOrderItem, localTypeValue, selectedTooth);
    if (foundAttribute) return; // if the attribute already exists, do nothing

    let localNameValue = defaultValue || '';

    /**
     * If there is an orderData, find the attribute name from the orderData, otherwise use the default value
     * this logic is only used for edit case scenarios where the attribute name is already set in the orderData
     */
    if (orderData) {
      const orderItemData = orderData.orderItems.find(orderItem => {
        // Check if the order item id matches the selected order item id or the selected order item itemId
        return orderItem.itemId === selectedOrderItem.itemId || orderItem.itemId === selectedOrderItem.id;
      });
      const orderItemAttribute = findAttributeCondition(orderItemData, localTypeValue, selectedTooth);
      localNameValue = orderItemAttribute?.name ?? localNameValue;
    }

    selectedOrderItem.attributes.push(getDefaultAttributeItem(localNameValue));

    updateOrderItem(selectedOrderItem.id, { attributes: selectedOrderItem.attributes });
  }, [
    defaultValue,
    getDefaultAttributeItem,
    localTypeValue,
    orderData,
    selectedOrderItem,
    selectedTooth,
    updateOrderItem,
  ]);

  /**
   * This is used to show the display attributes by default
   * this logic written to support the functionality of https://glidewell.atlassian.net/browse/LMS1-8564
   */
  const initialDisplayAttributes = useMemo(() => {
    if (!selectedOrderItem) return []; // if there is no selected order item, do nothing
    const displayAttributes = attributeRules?.flatMap(rule => rule.displayAttributes) || [];
    if (!displayAttributes.length) return []; // if there are no display attributes, do nothing

    const attributes = displayAttributes.map<ProductFullAttributeExtended | null>(displayAttribute => {
      const { name, autoSelectValue } = displayAttribute;
      const item = findProductFullAttributeByOption(productCode, { name });
      if (!item) return null;
      return { ...item, autoSelectValue };
    });

    // Filter out null values from the attributes array
    const filteredAttributes = attributes.filter((attribute): attribute is ProductFullAttributeExtended => {
      if (!attribute) return false;
      if (!nameValue) return true;
      return attribute.autoSelectValue === nameValue;
    });

    // Filter out the attributes that are not in the selected order item
    const notSelectedAttributes = attributes.filter(attribute => {
      if (!attribute) return false;
      return !filteredAttributes.some(item => item.name === attribute.name);
    });

    // Remove the attributes that are not available in the selected order item
    // and update the selected order item with the new attributes
    notSelectedAttributes.forEach(attribute => {
      if (!attribute) return;
      const findAttributeIndex = findAttributeConditionIndex(selectedOrderItem, attribute.name, selectedTooth);
      if (findAttributeIndex !== -1) {
        const updatedAttributes = [...selectedOrderItem.attributes];
        // remove the attribute from the list
        updatedAttributes.splice(findAttributeIndex, 1);
        // update the order item with the new attributes
        updateOrderItem(selectedOrderItem.id, {
          attributes: updatedAttributes,
        });
      }
    });

    return filteredAttributes; // return the filtered attributes
  }, [
    selectedOrderItem,
    attributeRules,
    findProductFullAttributeByOption,
    productCode,
    nameValue,
    selectedTooth,
    updateOrderItem,
  ]);

  const onUpdateNameValueLocal = useCallback(
    (value: string) => {
      onUpdateNameValue(value, nameValue);
      onValueChange?.(value, nameValue);
    },
    [nameValue, onUpdateNameValue, onValueChange]
  );

  return (
    <>
      <AttributeSelectDropdown
        label={displayName}
        options={options}
        value={nameValue}
        onValueChange={onUpdateNameValueLocal}
        isRequired={isRequired ?? false}
        isSubmitted={onSubmitAlertCount > 0}
        defaultValue={defaultValue || ''}
      />
      {isShowDisplayAttributeByDefault
        ? initialDisplayAttributes.map(attribute => {
            return (
              <InitialDisplayAttribute
                key={attribute.name}
                {...attribute}
                selectedTooth={selectedTooth}
                orderItemId={orderItemId}
                selectedOrderItem={selectedOrderItem}
                parentAttributeType={localTypeValue}
              />
            );
          })
        : nameValue && (
            <AggregateChildAttribute
              attributeName={nameValue}
              attributeType={attributeType}
              selectedTooth={selectedTooth}
              productCode={productCode}
              orderItemId={orderItemId}
            />
          )}
    </>
  );
};
